

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection, UiLabeledValue } from 'components/v2/record/DetailSection';

const LoyaltySummaryDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [summary, setSummary] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchSummaryData();
    }, [id]);

    const fetchSummaryData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/loyalty-summaries/${id}`);
            setSummary(response.data);
        } catch (error) {
            console.error('Error fetching loyalty summary data:', error);
            setSnackbar({ open: true, message: 'Error loading loyalty summary details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setSummary(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/loyalty-summaries/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Loyalty Summary updated successfully', severity: 'success' });
            fetchSummaryData();
        } catch (error) {
            console.error('Error updating loyalty summary:', error);
            setSnackbar({ open: true, message: 'Error updating loyalty summary', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this loyalty summary?')) {
            try {
                await axiosInstance.delete(`/loyalty-summaries/${id}`);
                setSnackbar({ open: true, message: 'Loyalty Summary deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/loyalty-summaries'), 1000);
            } catch (error) {
                console.error('Error deleting loyalty summary:', error);
                setSnackbar({ open: true, message: 'Error deleting loyalty summary', severity: 'error' });
            }
        }
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({
                open: true,
                message: `Unable to find user information for ${userName}.`,
                severity: 'info',
            });
        }
    };

    const capitalizeFirstLetter = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} md={6} key={item.field}>
                        {['created_by_name', 'updated_by_name'].includes(item.field) ? (
                            <UiLabeledValue label={item.label}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{item.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={() => {
                                        const userId = item.field === 'created_by_name' ? summary.created_by : summary.updated_by;
                                        handleUserClick(userId, summary[item.field]);
                                    }}
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{
                                        cursor: 'pointer',
                                        ml: 1
                                    }}
                                >
                                    {capitalizeFirstLetter(summary[item.field] || '')}
                                </MDTypography>
                            </MDBox>
                            </UiLabeledValue>
                        ) : (
                            <InlineEditField
                                label={item.label}
                                value={summary?.[item.field]}
                                options={item.options || null}
                                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                                isEditing={isEditing}
                                readOnly={item.readOnly || false}
                                isMultiSelect={item.isMultiSelect}
                                renderValue={item.renderValue || ((val) => (val ? String(val) : '-'))}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox py={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>
                
            </DashboardLayout>
        );
    }

    if (!summary) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox py={3}>
                    <MDTypography>Loyalty Summary not found</MDTypography>
                </MDBox>
                
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        {summary.name}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Loyalty Summary Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchSummaryData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Summary">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete Summary">
                                        <IconButton
                                            onClick={handleDelete}
                                            sx={commonIconButtonStyles}
                                        >
                                            <Delete sx={commonIconStyles} />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Basic Information", [
                                    { label: "Name", field: "name" },
                                    { label: "Customer", field: "customer_id", readOnly: true, renderValue: (val) => summary.customer?.name || val },
                                    { label: "Balance Points", field: "balance_points" },
                                    { label: "Lifetime Earned", field: "lifetime_earned" },
                                    { label: "Lifetime Redeemed", field: "lifetime_redeemed" },
                                    { label: "Lifetime Expired", field: "lifetime_expired" },
                                    { label: "Tier Progress Points", field: "tier_progress_points" },
                                    { label: "Tier Anniversary", field: "tier_anniversary", readOnly: true, renderValue: (val) => formatDate(val, 'date') },
                                    { label: "Last Earned On", field: "last_earned_on", readOnly: true, renderValue: (val) => formatDate(val, 'date') },
                                    { label: "Last Redeemed On", field: "last_redeemed_on", readOnly: true, renderValue: (val) => formatDate(val, 'date') },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name" },
                                    { label: "Modified By", field: "updated_by_name" },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default LoyaltySummaryDetail;

